import React from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'redux-form';
import { checkButtonDisabled } from '../../utils/forms';
import { addCommas } from '../../utils/common';
import ConfirmPriceDialog from './ConfirmPriceDialog';

export default class PriceBookTable extends React.Component {
  static propTypes = {
    form: PropTypes.string.isRequired,
    accountId: PropTypes.number.isRequired,
    dialog: PropTypes.object.isRequired,
    formValues: PropTypes.object,
    initialValues: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    invalid: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    buckets: PropTypes.object.isRequired,
    isCRM: PropTypes.bool.isRequired,
    isSuperAdmin: PropTypes.bool.isRequired,
    revShare: PropTypes.number.isRequired,
    saveChangesFunction: PropTypes.func.isRequired,
    closeDialogFunction: PropTypes.func.isRequired,
    openDialogFunction: PropTypes.func.isRequired,
    toggleForm: PropTypes.func.isRequired,
    updating: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    formValues: null,
    initialValues: null,
  };

  constructor(props) {
    super(props);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleOpenDialog = this.handleOpenDialog.bind(this);
    this.handleCloseDialog = this.handleCloseDialog.bind(this);
  }

  // Returns the table class with the correct styling
  getTableClass = isSuperAdmin => {
    const { updating } = this.props;

    if (isSuperAdmin) {
      if (updating) {
        return 'td-active';
      }
      return 'td-inactive';
    }

    if (updating) {
      return 'blur-table';
    }
    return null;
  };

  toCurrency = number => '$'.concat(number.toFixed(2));

  // Sizes the input field in the table correctly
  bucketSize = number => number.toFixed(2).toString().length - 1;

  // Formats table headers
  createHeaderString = (min, max) => {
    const { isCRM } = this.props;
    let headerString = '';
    if (min === 1) {
      headerString = '<'.concat(addCommas(max).toString());
    } else {
      headerString = addCommas(min)
        .toString()
        .concat(' - ')
        .concat(addCommas(max).toString());
    }
    if (!isCRM) {
      headerString = headerString.concat('K');
    }
    return headerString;
  };

  handleToggle = evt => {
    const { toggleForm } = this.props;
    toggleForm(evt);
  };

  handleOpenDialog = () => {
    const { accountId, isCRM, openDialogFunction } = this.props;
    // Creates a unique dialog id for each form on the admin page
    const dialogString = 'priceDialog'.concat(accountId).concat(isCRM);
    openDialogFunction(dialogString);
  };

  handleCloseDialog = () => {
    const { accountId, closeDialogFunction, isCRM } = this.props;
    // Creates a unique dialog id for each form on the admin page
    const dialogString = 'priceDialog'.concat(accountId).concat(isCRM);
    closeDialogFunction(dialogString);
  };

  renderButtons = updating => {
    const { submitting, invalid } = this.props;
    return (
      <span className="pull-right">
        {updating ? (
          <span>
            <button type="button" className="btn btn-secondary escape" onClick={this.handleToggle}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={checkButtonDisabled(invalid, submitting)}
            >
              Save
            </button>
          </span>
        ) : (
          <button type="button" className="btn btn-secondary escape" onClick={this.handleToggle}>
            Edit
          </button>
        )}
      </span>
    );
  };

  renderField = (field, cost) => (
    <Field
      name={field.toString()}
      type="text"
      component={this.renderInput}
      size={this.bucketSize(cost)}
    />
  );

  // Creates an input field for cost or floor
  renderInput = ({ input, type, size }) => (
    <p>
      $
      <input {...input} type={type} className="form-control-table" size={size} />
    </p>
  );

  renderBucket = bucketInfo => {
    const { isCRM, isSuperAdmin, revShare, updating } = this.props;
    const headerString = this.createHeaderString(bucketInfo.min, bucketInfo.max);
    const floorString = bucketInfo.id.toString().concat('_floor');

    return (
      <tr key={bucketInfo.id}>
        <th scope="row" className="pricing-table">
          {headerString}
        </th>
        {isSuperAdmin ? (
          <td className={updating ? 'td-active' : 'td-inactive'}>
            {updating
              ? this.renderField(bucketInfo.id, bucketInfo.cost)
              : this.toCurrency(bucketInfo.cost)}
          </td>
        ) : null}
        {isCRM || !isSuperAdmin ? null : (
          <td className={updating ? 'blur-table' : null}>{this.toCurrency(bucketInfo.cost / 2)}</td>
        )}
        {isSuperAdmin ? (
          <td className={updating ? 'blur-table' : null}>
            {this.toCurrency(bucketInfo.cost * revShare)}
          </td>
        ) : null}
        <td className={this.getTableClass(isSuperAdmin)}>
          {updating && isSuperAdmin
            ? this.renderField(floorString, bucketInfo.cost)
            : this.toCurrency(bucketInfo.floor)}
        </td>
        {isCRM && isSuperAdmin ? (
          <td className={updating ? 'blur-table' : null}>
            {(bucketInfo.floor / bucketInfo.cost * 100).toFixed(2)}%
          </td>
        ) : null}
      </tr>
    );
  };

  render() {
    const {
      form,
      accountId,
      buckets,
      dialog,
      formValues,
      initialValues,
      isCRM,
      isSuperAdmin,
      revShare,
      handleSubmit,
      updating,
      saveChangesFunction,
    } = this.props;
    const addMinWidth = !isCRM ? { minWidth: '950px' } : {};

    // Creates a unique dialog id for each form on the admin page
    const dialogString = 'priceDialog'.concat(accountId).concat(isCRM);

    return (
      <div className="flow-width-max">
        <div className="row">
          <div className="col-xs-12">
            <div className="card card-block" style={addMinWidth}>
              {buckets.size === 0 ? (
                <p>There is no {isCRM ? 'CRM' : 'MA'} pricebook associated with this account</p>
              ) : (
                <Form id={form} onSubmit={handleSubmit(this.handleOpenDialog)}>
                  <fieldset>
                    <div>
                      <p className="card-title">
                        <strong>{isCRM ? 'CRM' : 'MA'}</strong>
                        {isSuperAdmin ? this.renderButtons(updating) : null}
                      </p>
                      <table className="table table-reflow">
                        <thead>
                          <tr>
                            <th className="pricing-table">{isCRM ? 'Seats' : 'Records Sold'}</th>
                            {isSuperAdmin ? <td>{isCRM ? 'List per seat' : 'CPM'}</td> : null}
                            {isCRM || !isSuperAdmin ? null : (
                              <td className={updating ? 'blur-table' : null}>Monthly/M</td>
                            )}
                            {isSuperAdmin ? (
                              <td className={updating ? 'blur-table' : null}>
                                {revShare * 100}% Revenue Share
                              </td>
                            ) : null}
                            <td>
                              {/* Floor */}
                              {isCRM ? 'Price per seat' : 'Price per 1000'}
                            </td>
                            {isCRM && isSuperAdmin ? <td>&nbsp;</td> : null}
                          </tr>
                        </thead>
                        <tbody>{buckets.map(this.renderBucket)}</tbody>
                      </table>
                    </div>
                  </fieldset>
                </Form>
              )}
            </div>
          </div>
          {dialog.show && dialog.id === dialogString ? (
            <ConfirmPriceDialog
              dialog={dialog}
              buckets={buckets}
              initialValues={initialValues}
              formValues={formValues}
              form={form}
              handleSubmit={handleSubmit}
              isCRM={isCRM}
              isSuperAdmin={isSuperAdmin}
              closeDialogFunction={this.handleCloseDialog}
              submitPriceFunction={saveChangesFunction}
            />
          ) : null}
        </div>
      </div>
    );
  }
}
